import { createContext, useCallback, useContext, useState } from 'react'
import type { ReactNode } from 'react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { cn } from '@/lib/utils'

interface ConfirmOptions {
  title?: string
  message: string
  confirmLabel?: string
  cancelLabel?: string
  danger?: boolean
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>

const ConfirmContext = createContext<ConfirmFn | null>(null)

interface PendingConfirm extends ConfirmOptions {
  resolve: (value: boolean) => void
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingConfirm | null>(null)

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      setPending({ ...options, resolve })
    })
  }, [])

  const handle = (value: boolean) => {
    if (!pending) return
    pending.resolve(value)
    setPending(null)
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={!!pending} onOpenChange={(isOpen: boolean) => { if (!isOpen) handle(false) }}>
        <AlertDialogContent className="bg-surface border-border shadow-modal max-w-[420px]">
          <AlertDialogHeader>
            {pending?.title && (
              <AlertDialogTitle className="text-text-heading text-[1.05rem] font-bold">
                {pending.title}
              </AlertDialogTitle>
            )}
            <AlertDialogDescription className="text-text-2 text-[0.9rem] leading-relaxed">
              {pending?.message}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => handle(false)}
              className="bg-surface border-border-input text-text-2 hover:bg-surface-hover"
            >
              {pending?.cancelLabel ?? 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handle(true)}
              className={cn(
                'border-transparent font-semibold transition-opacity duration-200 hover:opacity-90',
                pending?.danger !== false
                  ? 'bg-danger-text text-danger-contrast'
                  : 'bg-accent text-accent-contrast',
              )}
            >
              {pending?.confirmLabel ?? 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  )
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext)
  if (!ctx) throw new Error('useConfirm must be used inside <ConfirmProvider>')
  return ctx
}
